import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PublicLayout from './public/PublicLayout';
import PaymentSuccessModal from './PaymentSuccessModal';

// Initialize Stripe with publishable key
const stripePromise = loadStripe('pk_live_51Sjj7yBLXCMScLjlM0vjwJqZI0QYKSItWv0yYLjyERA3Ryco5j702cye9eOhfZ36J2UCZyvkLUcxUZdkSxsPro7r00nfPvlATN');

// Local Mobile Money Paystack links per membership type (amounts in GHC)
const PAYSTACK_MEMBERSHIP_LINKS = {
  international: 'https://paystack.shop/pay/intl_arca',
  professional: 'https://paystack.shop/pay/profes',
  associate: 'https://paystack.shop/pay/asso',
  student: 'https://paystack.shop/pay/stude'
};

const MembershipPaymentPage = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Add member name state
  const [memberName, setMemberName] = useState('');
  const [nameError, setNameError] = useState('');

  // Get membership details from navigation state
  const membershipDetails = location.state || {};
  const { membershipType, membershipName, duration, amount } = membershipDetails;

  // Create payment intent when component mounts
  useEffect(() => {
    if (amount && membershipType && !clientSecret) {
      createPaymentIntent();
    }
  }, [amount, membershipType]);

  const createPaymentIntent = async () => {
    try {
      console.log('💳 Creating payment intent for:', membershipType, 'Amount:', amount);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/create-membership-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          membershipType,
          userId: user?.id || 'guest',
          email: user?.email || '',
          memberName: memberName || ''
        }),
      });

      const data = await response.json();
      console.log('📦 Payment intent response:', data);

      if (response.ok && data.success && data.clientSecret) {
        setClientSecret(data.clientSecret);
        setPaymentError('');
        console.log('✅ Client secret received, ready for payment');
      } else {
        console.error('❌ Payment intent failed:', data);
        const errorMsg = data.message || 'Failed to initialize payment system';
        setPaymentError(errorMsg);

        // Show user-friendly error
        if (data.errorCode === 'account_invalid' || data.errorCode === 'api_key_expired') {
          alert(`Payment System Configuration Issue\n\nThe card payment system is temporarily unavailable.\n\nPlease use one of these alternatives:\n1. Mobile Money (via Paystack)\n2. Bank Transfer\n\nOr contact support: admin@africaresp.org`);
        }
      }
    } catch (error) {
      console.error('❌ Error creating payment intent:', error);
      setPaymentError('Failed to connect to payment system');
    }
  };

  useEffect(() => {
    // If no membership details, redirect back to dashboard
    if (!membershipType || !amount) {
      navigate('/dashboard');
    }
  }, [membershipType, amount, navigate]);

  const handlePayment = async () => {
    // Validate member name before processing
    if (!memberName.trim()) {
      setNameError('Member full name is required');
      return;
    }

    setNameError('');
    setProcessing(true);

    try {
      // Handle Card Payment - Show embedded form
      if (paymentMethod === 'card') {
        if (!clientSecret) {
          alert('Payment system is initializing. Please wait a moment and try again.');
          setProcessing(false);
          return;
        }
        setShowPaymentForm(true);
        setProcessing(false);
        return;
      }

      // Handle Mobile Money - redirect to Paystack
      if (paymentMethod === 'mobile') {
        const paystackLink = PAYSTACK_MEMBERSHIP_LINKS[membershipType] || 'https://paystack.shop/pay/africaresp';
        window.open(paystackLink, '_blank');

        // Show instruction alert
        alert(`You will be redirected to Paystack for Mobile Money payment.
        
After completing payment, please return to this page.

Membership Type: ${membershipName}
Amount: $${amount}

An administrator will activate your membership after payment confirmation.`);

        // Close modal after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);

        setProcessing(false);
        return;
      }

      // Handle Bank Transfer - Show success message
      if (paymentMethod === 'bank') {
        alert(`Thank you for choosing Bank Transfer!

Please use the bank details shown on the page to complete your payment.

Membership Type: ${membershipName}
Amount: $${amount}

After payment, please email your payment confirmation to admin@africaresp.org

An administrator will activate your membership after payment confirmation.`);

        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);

        return;
      }

      // For other payment methods (PayPal)
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Show success message
      alert(`Payment of $${amount} processed successfully! 
      
Membership Type: ${membershipName}
Duration: ${duration}

Your payment has been received. An administrator will activate your membership shortly. You will receive a confirmation email once your membership is activated.`);

      // Redirect back to dashboard
      navigate('/dashboard');

    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (!membershipType || !amount) {
    return null; // Will redirect in useEffect
  }

  return (
    <PublicLayout>
      <div className="bg-light py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {/* Page Header */}
              <div className="mb-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="btn btn-outline-secondary btn-sm mb-3"
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Dashboard
                </button>
                <h1 className="h3 fw-bold text-dark">Complete Your Payment</h1>
                <p className="text-muted">Secure payment for your ARCA membership</p>
              </div>

              <div className="row g-4">
                {/* Order Summary */}
                <div className="col-lg-5">
                  <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                    <div className="card-header bg-white border-bottom">
                      <h5 className="mb-0 fw-bold">Order Summary</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <h6 className="fw-bold text-dark">{membershipName}</h6>
                        <p className="text-muted small mb-0">Duration: {duration}</p>
                      </div>

                      <hr />

                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Subtotal:</span>
                        <span className="fw-medium">${amount}.00</span>
                      </div>
                      <div className="d-flex justify-content-between mb-3">
                        <span className="text-muted">Tax:</span>
                        <span className="fw-medium">$0.00</span>
                      </div>

                      <hr />

                      <div className="d-flex justify-content-between mb-3">
                        <span className="fw-bold h5 mb-0">Total:</span>
                        <span className="fw-bold h5 mb-0" style={{ color: '#6B8E23' }}>${amount}.00</span>
                      </div>

                      <div className="alert alert-info small mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        Your membership will be activated by an administrator after payment confirmation.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Form */}
                <div className="col-lg-7">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom">
                      <h5 className="mb-0 fw-bold">Payment Method</h5>
                    </div>
                    <div className="card-body p-4">
                      {/* Payment Method Selection */}
                      <div className="mb-4">
                        <label className="form-label fw-bold">Select Payment Method</label>

                        {/* Card Payment (Visa/Mastercard/AmEx) */}
                        <div
                          className={`card mb-3 cursor-pointer ${paymentMethod === 'card' ? 'border-success border-2' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setPaymentMethod('card')}
                        >
                          <div className="card-body d-flex align-items-center">
                            <input
                              type="radio"
                              name="payment"
                              checked={paymentMethod === 'card'}
                              onChange={() => setPaymentMethod('card')}
                              className="form-check-input me-3"
                            />
                            <div className="flex-grow-1">
                              <h6 className="mb-0 fw-bold">Credit/Debit Card</h6>
                              <small className="text-muted">Visa, Mastercard, American Express via Stripe</small>
                            </div>
                            <div className="d-flex gap-2">
                              <i className="fab fa-cc-visa fa-2x text-primary"></i>
                              <i className="fab fa-cc-mastercard fa-2x" style={{ color: '#eb001b' }}></i>
                              <i className="fab fa-cc-amex fa-2x" style={{ color: '#006fcf' }}></i>
                            </div>
                          </div>
                        </div>

                        {/* Mobile Money */}
                        <div
                          className={`card mb-3 cursor-pointer ${paymentMethod === 'mobile' ? 'border-success border-2' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setPaymentMethod('mobile')}
                        >
                          <div className="card-body d-flex align-items-center">
                            <input
                              type="radio"
                              name="payment"
                              checked={paymentMethod === 'mobile'}
                              onChange={() => setPaymentMethod('mobile')}
                              className="form-check-input me-3"
                            />
                            <div className="flex-grow-1">
                              <h6 className="mb-0 fw-bold">Mobile Money</h6>
                              <small className="text-muted">M-Pesa, MTN, Airtel Money via Paystack</small>
                            </div>
                            <i className="fas fa-mobile-alt fa-2x" style={{ color: '#6B8E23' }}></i>
                          </div>
                        </div>

                        {/* Bank Transfer */}
                        <div
                          className={`card mb-3 cursor-pointer ${paymentMethod === 'bank' ? 'border-success border-2' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setPaymentMethod('bank')}
                        >
                          <div className="card-body d-flex align-items-center">
                            <input
                              type="radio"
                              name="payment"
                              checked={paymentMethod === 'bank'}
                              onChange={() => setPaymentMethod('bank')}
                              className="form-check-input me-3"
                            />
                            <div className="flex-grow-1">
                              <h6 className="mb-0 fw-bold">Bank Transfer</h6>
                              <small className="text-muted">Direct bank transfer (International & Local)</small>
                            </div>
                            <i className="fas fa-university fa-2x" style={{ color: '#6B8E23' }}></i>
                          </div>
                        </div>
                      </div>

                      {/* Payment Instructions */}
                      {/* Member Name Field - Required for all payment methods */}
                      <div className="mb-4">
                        <label htmlFor="memberName" className="form-label fw-bold">
                          Member Full Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="memberName"
                          className={`form-control form-control-lg ${nameError ? 'is-invalid' : ''}`}
                          placeholder="Enter your full name as it appears on your membership"
                          value={memberName}
                          onChange={(e) => {
                            setMemberName(e.target.value);
                            setNameError('');
                          }}
                          required
                        />
                        {nameError && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {nameError}
                          </div>
                        )}
                        <small className="form-text text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          This name will be used for payment records and receipts
                        </small>
                      </div>

                      {paymentError && (
                        <div className="alert alert-danger" role="alert">
                          <h6 className="fw-bold mb-2">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Payment System Error
                          </h6>
                          <p className="mb-2">{paymentError}</p>
                          <p className="small mb-0">
                            <strong>Alternative Options:</strong><br />
                            • Use Mobile Money payment via Paystack<br />
                            • Use Bank Transfer (details provided below)<br />
                            • Contact support: admin@africaresp.org
                          </p>
                        </div>
                      )}

                      {paymentMethod === 'card' && (
                        <div className="alert alert-success border-0" style={{ backgroundColor: '#f0f4e8' }}>
                          <h6 className="fw-bold mb-2">
                            <i className="fas fa-check-circle me-2" style={{ color: '#6B8E23' }}></i>
                            Secure Payment On This Page
                          </h6>
                          <p className="small mb-2">Complete your payment securely below without leaving our website.</p>
                          <p className="small mb-0 text-muted">
                            <i className="fas fa-shield-alt me-1"></i>
                            Accepts Visa, Mastercard, American Express. All payments are encrypted by Stripe.
                          </p>
                        </div>
                      )}

                      {paymentMethod === 'mobile' && (
                        <div className="alert alert-light">
                          <h6 className="fw-bold mb-2">Mobile Money Payment</h6>
                          <p className="small mb-2">Click the button below to pay via M-Pesa, MTN Mobile Money, or Airtel Money through Paystack.</p>
                          <p className="small mb-0 text-muted">You'll be redirected to Paystack to complete your mobile money payment.</p>
                        </div>
                      )}

                      {paymentMethod === 'bank' && (
                        <div className="alert alert-light">
                          <h6 className="fw-bold mb-3">Bank Transfer Details</h6>

                          {/* International Bank */}
                          <div className="mb-3 p-3 bg-white border rounded">
                            <h6 className="fw-bold text-primary mb-2"><i className="fas fa-globe me-2"></i>International Transfer</h6>
                            <div className="small">
                              <div className="row mb-1">
                                <div className="col-4 text-muted">Bank Name:</div>
                                <div className="col-8 fw-medium">Bank of America</div>
                              </div>
                              <div className="row mb-1">
                                <div className="col-4 text-muted">Account Name:</div>
                                <div className="col-8 fw-medium">Africa Respiratory Care Association Corp.</div>
                              </div>
                              <div className="row mb-1">
                                <div className="col-4 text-muted">Account No:</div>
                                <div className="col-8 fw-bold text-dark">334081495079</div>
                              </div>
                              <div className="row mb-1">
                                <div className="col-4 text-muted">Routing:</div>
                                <div className="col-8 fw-bold text-dark">026009593</div>
                              </div>
                              <div className="row mb-1">
                                <div className="col-4 text-muted">SWIFT:</div>
                                <div className="col-8 fw-bold text-dark">BOFAUS6S</div>
                              </div>
                              <div className="row">
                                <div className="col-4 text-muted">Address:</div>
                                <div className="col-8">Bank of America, N.A. 555 California St., San Francisco, CA 94104</div>
                              </div>
                            </div>
                          </div>

                          {/* Local Bank (Africa) */}
                          <div className="p-3 bg-white border rounded">
                            <h6 className="fw-bold text-success mb-2"><i className="fas fa-map-marker-alt me-2"></i>Local Transfer (Africa)</h6>
                            <div className="small">
                              <div className="row mb-1">
                                <div className="col-4 text-muted">Bank Name:</div>
                                <div className="col-8 fw-medium">Stanbic Bank Ghana</div>
                              </div>
                              <div className="row mb-1">
                                <div className="col-4 text-muted">Account Name:</div>
                                <div className="col-8 fw-medium">Africa Respiratory Care Association LBG</div>
                              </div>
                              <div className="row mb-1">
                                <div className="col-4 text-muted">Account No:</div>
                                <div className="col-8 fw-bold text-dark">9040012558435</div>
                              </div>
                              <div className="row mb-1">
                                <div className="col-4 text-muted">Branch:</div>
                                <div className="col-8 fw-medium">Graphic Road</div>
                              </div>
                              <div className="row mb-1">
                                <div className="col-4 text-muted">SWIFT:</div>
                                <div className="col-8 fw-bold text-dark">SBICGHAC</div>
                              </div>
                              <div className="row">
                                <div className="col-4 text-muted">Address:</div>
                                <div className="col-8">Stanbic Heights, No. 215 South Liberation Link, Airport City-Accra Ghana</div>
                              </div>
                            </div>
                          </div>

                          <div className="alert alert-warning mt-3 mb-0">
                            <small><i className="fas fa-info-circle me-2"></i>After completing the bank transfer, please email your payment confirmation to <strong>admin@africaresp.org</strong> with your membership details.</small>
                          </div>
                        </div>
                      )}

                      {/* Payment Button */}
                      {!showPaymentForm && (
                        <>
                          {/* Show loading state if payment system initializing */}
                          {paymentMethod === 'card' && !clientSecret && !paymentError && (
                            <div className="alert alert-info text-center">
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              Initializing secure payment system...
                            </div>
                          )}

                          <button
                            className="btn btn-lg w-100 text-white fw-bold mt-3"
                            style={{ backgroundColor: '#6B8E23', border: 'none' }}
                            onClick={handlePayment}
                            disabled={processing || (paymentMethod === 'card' && !clientSecret)}
                          >
                            {processing ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-lock me-2"></i>
                                Complete Payment ${amount}
                              </>
                            )}
                          </button>
                        </>
                      )}

                      {/* Embedded Stripe Payment Form */}
                      {showPaymentForm && clientSecret && (
                        <div className="mt-4">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-bold">
                              <i className="fas fa-credit-card me-2" style={{ color: '#6B8E23' }}></i>
                              Enter Payment Details
                            </h6>
                            <button
                              onClick={() => setShowPaymentForm(false)}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              <i className="fas fa-arrow-left me-1"></i>
                              Back
                            </button>
                          </div>
                          <Elements stripe={stripePromise} options={{ clientSecret }}>
                            <CheckoutForm
                              amount={amount}
                              memberName={memberName}
                              onSuccess={() => {
                                setShowSuccessModal(true);
                                // Reload after 3 seconds to show receipt
                                setTimeout(() => {
                                  navigate('/dashboard');
                                }, 3000);
                              }}
                              onError={(error) => {
                                setPaymentError(error.message);
                              }}
                            />
                          </Elements>
                        </div>
                      )}

                      <div className="text-center mt-3">
                        <small className="text-muted">
                          <i className="fas fa-shield-alt me-1"></i>
                          Secure payment powered by Stripe
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Success Modal */}
      <PaymentSuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        paymentType="membership"
        details={{
          name: membershipName,
          duration: duration,
          amount: amount
        }}
      />
    </PublicLayout>
  );
};

// Embedded Payment Form Component
const CheckoutForm = ({ amount, memberName, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
          payment_method_data: {
            billing_details: {
              name: memberName
            }
          }
        },
      });

      if (error) {
        setErrorMessage(error.message);
        setIsProcessing(false);
        if (onError) onError(error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('✅ Payment succeeded:', paymentIntent.id);
        if (onSuccess) onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error('❌ Payment error:', err);
      setErrorMessage('An unexpected error occurred');
      setIsProcessing(false);
      if (onError) onError(err);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <PaymentElement />
      </div>

      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="btn btn-lg w-100 text-white fw-bold"
        style={{ backgroundColor: '#6B8E23', border: 'none' }}
      >
        {isProcessing ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Processing Payment...
          </>
        ) : (
          <>
            <i className="fas fa-lock me-2"></i>
            Pay ${amount}
          </>
        )}
      </button>

      <div className="text-center mt-3">
        <small className="text-muted">
          <i className="fas fa-shield-alt me-1"></i>
          Your payment is secured by Stripe
        </small>
      </div>
    </form>
  );
};

export default MembershipPaymentPage;
